import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import { IMfaApiResponse, IMfaSetupData, TMfaType } from './mfaTypes';

interface IRequestChangeMfaInput {
  type: TMfaType;
  token?: string;
}

const initialState: IApiState<IMfaApiResponse<IMfaSetupData>> = {
  value: null,
  status: 'idle',
  error: '',
};

export const requestChangeMfaAsync = createAsyncThunk(
  'settings/mfa/request-change',
  async (
    { type, token }: IRequestChangeMfaInput,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const { data } = await api.post<IMfaApiResponse<IMfaSetupData>>(
        '/settings/mfa/request-change',
        { type, ...(token ? { token } : {}) },
        {
          headers: {
            Authorization: `Token ${accessToken}`,
            'X-Business-Id': businessId,
          },
        },
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IMfaApiResponse<IMfaSetupData>>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(showAlert({ status: 'error', description: errorMessage }));

      return rejectWithValue(error.response?.data);
    }
  },
);

export const requestChangeMfaSlice = createSlice({
  name: 'settings/mfa/request-change',
  initialState,
  reducers: {
    resetRequestChangeMfa: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestChangeMfaAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(requestChangeMfaAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(requestChangeMfaAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IMfaApiResponse<IMfaSetupData>;
        state.error = action.error.message;
      });
  },
});

export const { resetRequestChangeMfa } = requestChangeMfaSlice.actions;

export default requestChangeMfaSlice.reducer;
